import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Typography, Spin, Alert, Divider, Steps } from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  SafetyCertificateOutlined, 
  BookOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  StarOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import '../AuthPage.css';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

/**
 * Student onboarding authentication page
 */
const StudentAuthPage = () => {
  const { user, isAuthenticated, isLoading, error, login, register, clearError, setError } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const nextUrl = new URLSearchParams(location.search).get('next') || '/admin';
      history.replace(nextUrl);
    }
  }, [isAuthenticated, user, history, location]);

  // Handle callback errors
  useEffect(() => {
    const error = new URLSearchParams(location.search).get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      
      switch (error) {
        case 'missing_tokens':
          errorMessage = 'Authentication tokens were not received. Please try again.';
          break;
        case 'session_validation_failed':
          errorMessage = 'Session validation failed. Please try again.';
          break;
        case 'session_validation_error':
          errorMessage = 'Error validating your session. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'Error during authentication. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${error}. Please try again.`;
      }
      
      clearError();
      setTimeout(() => {
        setError(errorMessage);
      }, 100);
    }
  }, [location, clearError, setError]);

  // Handle OIDC login with student role context
  const handleOIDCLogin = () => {
    clearError();
    login("student");
  };

  // Handle OIDC registration with student role context
  const handleOIDCRegister = () => {
    clearError();
    register("student");
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spin size="large" />
        <div className="auth-loading-text">
          <Text>Verifying your session...</Text>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-content">
            <div className="auth-header">
              <SafetyCertificateOutlined className="auth-icon error" />
              <Title level={2} className="auth-title">
                Authentication Error
              </Title>
            </div>
            
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              className="auth-error"
            />
            
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button"
              icon={<UserOutlined />}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main student onboarding page
  return (
    <div className="auth-container">
      <Card className="auth-card onboarding-card">
        <div className="auth-content">
          {/* Header */}
          <div className="auth-header">
            <BookOutlined className="auth-icon student-icon" />
            <Title level={1} className="auth-title">
              Start Your Learning Journey
            </Title>
            <Text className="auth-subtitle">
              Access courses, track progress, and achieve your educational goals
            </Text>
          </div>

          {/* Onboarding Steps */}
          <div className="onboarding-steps">
            <Steps size="small" current={0}>
              <Step 
                title="Join" 
                icon={<UserOutlined />} 
                description="Create your account"
              />
              <Step 
                title="Learn" 
                icon={<BookOutlined />} 
                description="Access your courses"
              />
              <Step 
                title="Excel" 
                icon={<TrophyOutlined />} 
                description="Track your progress"
              />
            </Steps>
          </div>

          {/* Benefits */}
          <div className="onboarding-benefits">
            <Title level={4}>What awaits you:</Title>
            <div className="benefits-list">
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Interactive courses and assessments</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Real-time progress tracking</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Personalized learning paths</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Achievement badges and certificates</Text>
              </div>
            </div>
          </div>

          {/* Auth Actions */}
          <div className="auth-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCRegister}
              className="auth-button oidc-button primary-cta"
              icon={<StarOutlined />}
              loading={isLoading}
            >
              Join Now - It&apos;s Free!
            </Button>
            
            <Divider>Already registered?</Divider>
            
            <Button 
              type="default" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button login-button"
              icon={<LockOutlined />}
            >
              Sign In to Continue Learning
            </Button>
          </div>

          {/* Security Info */}
          <div className="auth-info-section">
            <Paragraph className="auth-info">
              <SafetyCertificateOutlined /> Your privacy and data security are our top priority
            </Paragraph>
            <Paragraph className="auth-info small">
              Secure authentication powered by industry-standard protocols
            </Paragraph>
          </div>

          {/* Footer */}
          <div className="auth-footer">
            <Text type="secondary">
              Questions about your course? <a href="mailto:student-support@strideahead.com">Get help here</a>
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default StudentAuthPage;